/**
 * The SLOC cascade both forms use: pick a WH (a location — WH-01), then a Bin under it, then a Sub
 * Bin under that. Built once from the flat `/locations/tree` rows; the warehouse above the WH is
 * implied by the plant and never offered.
 *
 * Stock goes on a leaf: a sub-bin, or a bin that has no sub-bins (its Sub Bin step is then empty).
 */
export interface SlocTreeNode {
  id: number;
  parentId: number | null;
  kind: string;
  code: string;
  path: string;
  isActive: boolean;
}

export interface SlocTrail {
  whId: number;
  binId: number;
  /** Null when the bin itself is the leaf. */
  subBinId: number | null;
}

export interface SlocTree {
  /** Active locations at the plant, by path. */
  whs: SlocTreeNode[];
  binsOf(whId: number): SlocTreeNode[];
  subBinsOf(binId: number): SlocTreeNode[];
  isLeaf(id: number): boolean;
  /** Where a saved leaf sits, to pre-fill the three selects; null if it isn't a leaf here. */
  trail(leafId: number): SlocTrail | null;
}

const collator = new Intl.Collator("en", { numeric: true });
const byCode = (a: SlocTreeNode, b: SlocTreeNode) =>
  collator.compare(a.code, b.code);

export function buildSlocTree(nodes: readonly SlocTreeNode[]): SlocTree {
  const byId = new Map<number, SlocTreeNode>();
  const kids = new Map<number, SlocTreeNode[]>();
  for (const n of nodes) {
    if (!n.isActive) continue;
    byId.set(n.id, n);
  }
  for (const n of byId.values()) {
    if (n.parentId === null || !byId.has(n.parentId)) continue;
    const list = kids.get(n.parentId);
    if (list) list.push(n);
    else kids.set(n.parentId, [n]);
  }
  for (const list of kids.values()) list.sort(byCode);
  const childrenOf = (id: number, kind: string) =>
    (kids.get(id) ?? []).filter((n) => n.kind === kind);

  const whs = [...byId.values()]
    .filter((n) => n.kind === "location")
    .sort((a, b) => collator.compare(a.path, b.path));
  const isLeaf = (id: number) => {
    const n = byId.get(id);
    if (!n) return false;
    if (n.kind === "sub_bin") return true;
    return n.kind === "bin" && childrenOf(id, "sub_bin").length === 0;
  };

  return {
    whs,
    binsOf: (whId) => childrenOf(whId, "bin"),
    subBinsOf: (binId) => childrenOf(binId, "sub_bin"),
    isLeaf,
    trail(leafId) {
      if (!isLeaf(leafId)) return null;
      const leaf = byId.get(leafId)!;
      const bin = leaf.kind === "bin" ? leaf : byId.get(leaf.parentId ?? -1);
      const wh = bin && byId.get(bin.parentId ?? -1);
      if (!bin || bin.kind !== "bin" || !wh || wh.kind !== "location")
        return null;
      return {
        whId: wh.id,
        binId: bin.id,
        subBinId: leaf.kind === "sub_bin" ? leaf.id : null,
      };
    },
  };
}
